import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { getApiBase } from "../apiBase";
import { isCvinExpansion, formatExpansionWorkOrderForDisplay } from "../projectGridColumns";
import { displayPhaseForRow } from "../projectPhaseDisplay";
import { readSubsegmentSummaryFromAcf } from "./expansionProjectAcf";
import {
  canEditExpansionSegmentFinancials,
  canViewExpansionSegmentFinancials,
  canViewFullExpansionConstructionContractFields,
} from "./expansionFieldAccess";
import {
  fetchExpansionSegmentFinancials,
  putExpansionSegmentFinancials,
} from "./expansionSegmentFinancialsApi";
import {
  EXPANSION_SEGMENT_LANDING_FIELD_DEFS,
  EXPANSION_SEGMENT_STORED_FIELD_DEFS,
  emptyExpansionSegmentFinancials,
} from "./expansionSegmentFinancialsSchema";
import {
  buildExpansionSegmentDetailRow,
  downloadExpansionSegmentDetailCsv,
} from "./expansionSegmentDetailExport";
import { formatContractCost } from "./contractsAccess";
import { fetchBatchProjectContractFields } from "./contractsApi";
import { formatContractorsForRole } from "./contractsDerivedFields";
import { isPluginEnabled, PLUGIN_IDS } from "./registry";
import {
  computeExpansionGroupStats,
  computeExpansionGroupFootageProgress,
  filterExpansionRowsForBase,
  formatExpansionGroupTitle,
  isExpansionParentShellRow,
  segmentFromSubsegmentPrefix,
} from "./expansionGroupUtils";
import ExpansionWorkOrderMapPreview from "./ExpansionWorkOrderMapPreview";
import ExpansionFootageProgressBar, { formatFootageNumber } from "./ExpansionFootageProgressBar";
import { computeFootageProgressForProject } from "./expansionProgressUtils";
import {
  aggregateSegmentMaterialTotals,
  collectPermitMaterialsFromProjectRow,
  EngineeringMaterialsSummary,
} from "./engineeringMaterialsTotals";
import { getViewerEmailFromStorage } from "./userMatrixStorage";
import { canAddExpansionSubsegment } from "./commercialRoleAccess";
import "./expansionSegmentPage.css";

import { formatDateOnlyDisplay } from "./dateOnlyDisplay";

function formatContractDate(value) {
  if (!value) return "—";
  const formatted = formatDateOnlyDisplay(value);
  return formatted || String(value);
}

function contractFieldsForProject(projectId, contractFieldsByProjectId) {
  return contractFieldsByProjectId?.[projectId] || null;
}

function contractorRoleLabels(summary, { includeCoverage = false } = {}) {
  if (!summary) {
    return { construction: "—", fiber: "—" };
  }
  const construction =
    formatContractorsForRole(summary, "construction", {
      includeCoverage,
      formatFootage: formatFootageNumber,
    }) || "";
  const fiber =
    formatContractorsForRole(summary, "fiber", {
      includeCoverage,
      formatFootage: formatFootageNumber,
    }) || "";
  return {
    construction: construction || "—",
    fiber: fiber || "—",
  };
}

const EMPTY_FINANCIALS = emptyExpansionSegmentFinancials();

const READ_ONLY_FINANCIAL_KEYS = new Set(
  EXPANSION_SEGMENT_STORED_FIELD_DEFS.filter((field) => field.readOnly).map((field) => field.key)
);

function formatSegmentDetailCell(header, value) {
  if (value == null || value === "") return "—";
  if (header === "% Awarded") {
    const n = Number(value);
    return Number.isFinite(n) ? `${(n * 100).toFixed(1)}%` : String(value);
  }
  if (
    header.includes("$") ||
    header.includes("Cost") ||
    header.includes("Budget") ||
    header.includes("Allocation") ||
    header.includes("Variance")
  ) {
    const n = Number(value);
    return Number.isFinite(n)
      ? n.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 2 })
      : String(value);
  }
  const n = Number(value);
  if (Number.isFinite(n)) return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
  return String(value);
}

export function ExpansionSegmentGate({ apiUrl, getAuthHeaders }) {
  return <ExpansionSegmentPage apiUrl={apiUrl} getAuthHeaders={getAuthHeaders} />;
}

export default function ExpansionSegmentPage({ apiUrl, getAuthHeaders }) {
  const { base } = useParams();
  const viewerEmail = getViewerEmailFromStorage();
  const canAddSubsegment = canAddExpansionSubsegment(viewerEmail);
  const canViewFinancials = canViewExpansionSegmentFinancials(viewerEmail);
  const canEditFinancials = canEditExpansionSegmentFinancials(viewerEmail);
  const contractsEnabled = isPluginEnabled(PLUGIN_IDS.CONTRACTS);
  const canViewFullContractDetails = canViewFullExpansionConstructionContractFields(viewerEmail);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [rows, setRows] = useState([]);
  const [financials, setFinancials] = useState(EMPTY_FINANCIALS);
  const [financialDraft, setFinancialDraft] = useState(EMPTY_FINANCIALS);
  const [financialsLoading, setFinancialsLoading] = useState(false);
  const [financialsError, setFinancialsError] = useState("");
  const [financialsEditing, setFinancialsEditing] = useState(false);
  const [financialsSaving, setFinancialsSaving] = useState(false);
  const [contractFieldsByProjectId, setContractFieldsByProjectId] = useState({});
  const [contractsLoading, setContractsLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const url = apiUrl || getApiBase();
      const { data } = await axios.get(`${url}/projects`, { headers: getAuthHeaders() });
      const all = Array.isArray(data) ? data.filter(isCvinExpansion) : [];
      setRows(filterExpansionRowsForBase(all, base));
    } catch (err) {
      setError(err?.response?.data?.error || err.message || "Could not load segment.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [apiUrl, base, getAuthHeaders]);

  const loadFinancials = useCallback(async () => {
    if (!canViewFinancials) return;
    setFinancialsLoading(true);
    setFinancialsError("");
    try {
      const data = await fetchExpansionSegmentFinancials(base);
      const next = { ...EMPTY_FINANCIALS, ...data };
      setFinancials(next);
    } catch (err) {
      setFinancialsError(err?.response?.data?.error || err.message || "Could not load budget fields.");
      setFinancials(EMPTY_FINANCIALS);
    } finally {
      setFinancialsLoading(false);
    }
  }, [base, canViewFinancials]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    void loadFinancials();
  }, [loadFinancials]);

  const loadContractFields = useCallback(async () => {
    if (!contractsEnabled || !rows.length) {
      setContractFieldsByProjectId({});
      return;
    }
    setContractsLoading(true);
    try {
      const projectIds = rows.map((r) => r.id).filter(Boolean);
      const result = await fetchBatchProjectContractFields(projectIds, {
        apiUrl,
        getAuthHeaders,
      });
      setContractFieldsByProjectId(result?.byProjectId || {});
    } catch {
      setContractFieldsByProjectId({});
    } finally {
      setContractsLoading(false);
    }
  }, [apiUrl, contractsEnabled, getAuthHeaders, rows]);

  useEffect(() => {
    void loadContractFields();
  }, [loadContractFields]);

  useEffect(() => {
    if (!financialsEditing) setFinancialDraft(financials);
  }, [financials, financialsEditing]);

  const segmentMaterialTotals = useMemo(() => aggregateSegmentMaterialTotals(rows), [rows]);
  const subsegmentsWithMaterials = useMemo(() => {
    let count = 0;
    for (const row of rows) {
      const wo = String(row?.work_order ?? "").trim();
      if (wo && !wo.includes(".")) continue;
      if (collectPermitMaterialsFromProjectRow(row).length) count += 1;
    }
    return count;
  }, [rows]);

  const segment = useMemo(() => segmentFromSubsegmentPrefix(rows), [rows]);
  const stats = useMemo(() => computeExpansionGroupStats(rows), [rows]);
  const installationProgress = useMemo(() => computeExpansionGroupFootageProgress(rows), [rows]);
  const title = useMemo(() => formatExpansionGroupTitle(base, segment), [base, segment]);

  const phaseRows = useMemo(() => {
    const counts = stats.phases || {};
    return Object.entries(counts).sort((a, b) => a[0].localeCompare(b[0]));
  }, [stats.phases]);

  const sortedChildren = useMemo(
    () =>
      [...rows].sort((a, b) => {
        const aParent = isExpansionParentShellRow(a);
        const bParent = isExpansionParentShellRow(b);
        if (aParent !== bParent) return aParent ? -1 : 1;
        return formatExpansionWorkOrderForDisplay(a).localeCompare(
          formatExpansionWorkOrderForDisplay(b),
          undefined,
          { numeric: true, sensitivity: "base" }
        );
      }),
    [rows]
  );

  const segmentDetailRow = useMemo(
    () =>
      buildExpansionSegmentDetailRow({
        woBase: base,
        segmentLabel: segment,
        children: rows,
        financialsRaw: financials,
        contractFieldsByProjectId: contractsEnabled ? contractFieldsByProjectId : null,
      }),
    [base, segment, rows, financials, contractsEnabled, contractFieldsByProjectId]
  );

  const onFinancialFieldChange = (fieldDef, raw) => {
    const trimmed = String(raw ?? "").trim();
    if (fieldDef.type === "text") {
      setFinancialDraft((prev) => ({ ...prev, [fieldDef.key]: trimmed === "" ? null : trimmed }));
      return;
    }
    const value = trimmed === "" ? null : Number(trimmed);
    setFinancialDraft((prev) => ({
      ...prev,
      [fieldDef.key]: Number.isFinite(value) ? value : null,
    }));
  };

  const toggleFinancialsEdit = async () => {
    if (!canEditFinancials) return;
    if (financialsEditing) {
      setFinancialsSaving(true);
      setFinancialsError("");
      try {
        const payload = { ...financialDraft };
        for (const key of READ_ONLY_FINANCIAL_KEYS) {
          delete payload[key];
        }
        const saved = await putExpansionSegmentFinancials(base, payload);
        const next = { ...EMPTY_FINANCIALS, ...saved };
        setFinancials(next);
        setFinancialDraft(next);
        setFinancialsEditing(false);
      } catch (err) {
        setFinancialsError(err?.response?.data?.error || err.message || "Could not save budget fields.");
      } finally {
        setFinancialsSaving(false);
      }
      return;
    }
    setFinancialDraft(financials);
    setFinancialsEditing(true);
  };

  const cancelFinancialsEdit = () => {
    setFinancialDraft(financials);
    setFinancialsEditing(false);
    setFinancialsError("");
  };

  const displayFinancials = financialsEditing ? financialDraft : financials;

  if (loading) {
    return (
      <div className="expansion-segment-shell">
        <div className="expansion-segment-page">
          <p className="project-detail-muted">Loading segment…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="expansion-segment-shell">
        <div className="expansion-segment-page">
          <Link to="/" className="project-detail-back">
            ← Back to board
          </Link>
          <p className="project-detail-error">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="expansion-segment-shell">
      <div className="expansion-segment-page">
      <Link to="/" className="project-detail-back">
        ← Back to expansion board
      </Link>

      <header className="expansion-segment-hero">
        <div>
          <p className="expansion-segment-eyebrow">Expansion segment</p>
          <h1 className="expansion-segment-title">{title}</h1>
          <p className="project-detail-muted expansion-segment-subtitle">
            {stats.projectCount} subsegment{stats.projectCount === 1 ? "" : "s"} · Parent work order {base}
          </p>
          {canAddSubsegment ? (
            <p style={{ margin: "12px 0 0" }}>
              <Link
                to={`/expansion/segment/${encodeURIComponent(base)}/add`}
                className="add-project-submit"
                style={{ display: "inline-block", textDecoration: "none" }}
              >
                + Add subsegment
              </Link>
            </p>
          ) : null}
        </div>
        {stats.percentComplete != null ? (
          <div className="expansion-segment-progress-badge" aria-label="Percent complete">
            <span className="expansion-segment-progress-value">{stats.percentComplete}%</span>
            <span className="expansion-segment-progress-label">Complete</span>
          </div>
        ) : null}
      </header>

      <section className="expansion-segment-panel expansion-segment-installation-panel">
        <h2 className="expansion-segment-panel-title">Installation progress</h2>
        <ExpansionFootageProgressBar
          progress={installationProgress}
          height={14}
          className="expansion-segment-installation-bar"
        />
        <p className="project-detail-muted expansion-segment-installation-caption">
          Conduit target is construction footage; fiber target is override or construction footage per
          subsegment. Subsegments with CDT in the title are excluded from overall %.
          {installationProgress.conduitDenominator != null
            ? ` Conduit ${formatFootageNumber(installationProgress.conduitCompleted)} of ${formatFootageNumber(installationProgress.conduitDenominator)} ft.`
            : ""}
          {installationProgress.fiberDenominator != null
            ? ` Fiber ${formatFootageNumber(installationProgress.fiberCompleted)} of ${formatFootageNumber(installationProgress.fiberDenominator)} ft.`
            : ""}
        </p>
      </section>

      {canViewFinancials ? (
        <section className="expansion-segment-panel expansion-segment-financial-panel">
          <div className="expansion-segment-financial-head">
            <h2 className="expansion-segment-panel-title">Segment detail</h2>
            <div className="expansion-segment-financial-actions">
              <button
                type="button"
                className="expansion-segment-financial-btn expansion-segment-financial-btn--ghost"
                onClick={() => downloadExpansionSegmentDetailCsv([segmentDetailRow])}
              >
                Export CSV
              </button>
              {canEditFinancials ? (
                <>
                  {financialsEditing ? (
                    <button
                      type="button"
                      className="expansion-segment-financial-btn expansion-segment-financial-btn--ghost"
                      disabled={financialsSaving}
                      onClick={cancelFinancialsEdit}
                    >
                      Cancel
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className="expansion-segment-financial-btn"
                    disabled={financialsSaving || financialsLoading}
                    onClick={() => void toggleFinancialsEdit()}
                  >
                    {financialsSaving ? "Saving…" : financialsEditing ? "Save" : "Edit"}
                  </button>
                </>
              ) : null}
            </div>
          </div>
          {financialsError ? <p className="project-detail-error">{financialsError}</p> : null}
          {financialsLoading || (contractsEnabled && contractsLoading) ? (
            <p className="project-detail-muted">Loading segment detail…</p>
          ) : (
            <div className="expansion-segment-financial-grid">
              {EXPANSION_SEGMENT_LANDING_FIELD_DEFS.map((field) => {
                const stored = field.stored;
                const isEditable = Boolean(financialsEditing && stored && !stored.readOnly);
                return (
                  <div
                    key={field.header}
                    className={`expansion-segment-financial-field${
                      field.computed ? " expansion-segment-financial-field--computed" : ""
                    }`}
                  >
                    <span className="expansion-segment-financial-label">{field.label}</span>
                    {isEditable ? (
                      <input
                        type={stored.type === "number" ? "number" : "text"}
                        className="expansion-segment-financial-input"
                        step={stored.step}
                        value={displayFinancials[stored.key] ?? ""}
                        onChange={(e) => onFinancialFieldChange(stored, e.target.value)}
                      />
                    ) : (
                      <span className="expansion-segment-financial-value">
                        {formatSegmentDetailCell(field.header, segmentDetailRow[field.header])}
                      </span>
                    )}
                    {field.hint ? (
                      <span className="expansion-segment-financial-hint">{field.hint}</span>
                    ) : null}
                  </div>
                );
              })}
            </div>
          )}
        </section>
      ) : null}

      <section className="expansion-segment-panel expansion-segment-materials-panel">
        <h2 className="expansion-segment-panel-title">Material totals</h2>
        <p className="project-detail-muted expansion-segment-materials-desc">
          Rolled up from permit materials on {subsegmentsWithMaterials} subsegment
          {subsegmentsWithMaterials === 1 ? "" : "s"} in this segment.
        </p>
        <EngineeringMaterialsSummary
          rows={segmentMaterialTotals}
          limit={null}
          emptyMessage="No permit materials entered across subsegments yet."
        />
      </section>

      <div className="expansion-segment-mid-row">
        <section className="expansion-segment-panel">
          <h2 className="expansion-segment-panel-title">Phase distribution</h2>
          {phaseRows.length === 0 ? (
            <p className="project-detail-muted">No phase data yet.</p>
          ) : (
            <ul className="expansion-segment-breakdown">
              {phaseRows.map(([phase, count]) => (
                <li key={phase}>
                  <span>{phase}</span>
                  <strong>{count}</strong>
                </li>
              ))}
            </ul>
          )}
        </section>
        <section className="expansion-segment-panel expansion-segment-panel-map">
          <h2 className="expansion-segment-panel-title">Segment map</h2>
          <ExpansionWorkOrderMapPreview workOrder={base} />
        </section>
      </div>

      <section className="expansion-segment-panel expansion-segment-table-panel">
        <h2 className="expansion-segment-panel-title">Subsegments</h2>
        {sortedChildren.length === 0 ? (
          <p className="project-detail-muted">No subsegments found for this work order.</p>
        ) : (
          <div className="expansion-segment-table-wrap">
            <table className="expansion-segment-table">
              <thead>
                <tr>
                  <th>Work order</th>
                  <th>Project</th>
                  <th>Phase</th>
                  <th>Override ft</th>
                  <th>Construction ft</th>
                  <th>Conduit installed</th>
                  <th>Fiber installed</th>
                  <th>Proofing completed</th>
                  <th>Handholes</th>
                  {contractsEnabled ? (
                    <>
                      <th>Construction contractor</th>
                      <th>Fiber contractor</th>
                      <th>Executed</th>
                      <th>Expires</th>
                      {canViewFullContractDetails ? <th>Contract cost</th> : null}
                    </>
                  ) : null}
                </tr>
              </thead>
              <tbody>
                {sortedChildren.map((row) => {
                  const sub = readSubsegmentSummaryFromAcf(row.acf_groups_json);
                  const progress = computeFootageProgressForProject(row);
                  const summary = contractFieldsForProject(row.id, contractFieldsByProjectId);
                  const fields = summary?.fields || null;
                  const contractors = contractorRoleLabels(summary, { includeCoverage: true });
                  const parentShell = isExpansionParentShellRow(row);
                  return (
                    <tr key={row.id} className={parentShell ? "expansion-segment-parent-shell-row" : undefined}>
                      <td>
                        <Link to={`/project/${row.id}`} className="expansion-segment-wo-link">
                          {formatExpansionWorkOrderForDisplay(row) || row.id}
                        </Link>
                        {parentShell ? (
                          <span className="expansion-segment-parent-shell-badge">Parent WO reports</span>
                        ) : null}
                      </td>
                      <td>{sub?.project_name ?? row.name ?? "—"}</td>
                      <td>{displayPhaseForRow(row) || "—"}</td>
                      <td>{sub?.override_footage ?? "—"}</td>
                      <td>{sub?.construction_footage ?? "—"}</td>
                      <td>{formatFootageNumber(progress.conduitCompleted)}</td>
                      <td>{formatFootageNumber(progress.fiberCompleted)}</td>
                      <td>{formatFootageNumber(progress.proofingCompleted)}</td>
                      <td>{sub?.handholes ?? "—"}</td>
                      {contractsEnabled ? (
                        <>
                          <td>{contractsLoading && !summary ? "…" : contractors.construction}</td>
                          <td>{contractsLoading && !summary ? "…" : contractors.fiber}</td>
                          <td>{formatContractDate(fields?.contract_execution_date)}</td>
                          <td>{formatContractDate(fields?.contract_expiration_date)}</td>
                          {canViewFullContractDetails ? (
                            <td>{formatContractCost(fields?.contract_cost)}</td>
                          ) : null}
                        </>
                      ) : null}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
      </div>
    </div>
  );
}
